import React, { useState } from 'react';
import { products, Product } from '../data/products';

interface TreeNode {
  id: string;
  label: string;
  level: 'root' | 'handSize' | 'handedness' | 'userType' | 'results';
  value?: string;
  children?: TreeNode[];
  products?: Product[];
  parentPath?: string[];
}

interface CollapsedState {
  [key: string]: boolean;
}

const DecisionTreeViewer: React.FC = () => {
  const [expandedNodes, setExpandedNodes] = useState<CollapsedState>({
    root: true,
  });
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  // Build the decision tree
  const buildTree = (): TreeNode => {
    const handSizes = ['small', 'medium', 'large'];
    const handedness = ['right', 'left'];
    const userTypes = ['professional', 'amateur'];

    const root: TreeNode = {
      id: 'root',
      label: 'Decision Tree',
      level: 'root',
      children: [],
    };

    handSizes.forEach((size) => {
      const sizeNode: TreeNode = {
        id: `handSize-${size}`,
        label: `Hand Size: ${size.charAt(0).toUpperCase() + size.slice(1)}`,
        level: 'handSize',
        value: size,
        children: [],
        parentPath: ['handSize'],
      };

      handedness.forEach((hand) => {
        const handNode: TreeNode = {
          id: `handedness-${size}-${hand}`,
          label: `Handedness: ${hand.charAt(0).toUpperCase() + hand.slice(1)}-handed`,
          level: 'handedness',
          value: hand,
          children: [],
          parentPath: ['handSize', size, 'handedness'],
        };

        userTypes.forEach((userType) => {
          const userTypeNode: TreeNode = {
            id: `userType-${size}-${hand}-${userType}`,
            label: `User Type: ${userType.charAt(0).toUpperCase() + userType.slice(1)}`,
            level: 'userType',
            value: userType,
            products: [],
            parentPath: ['handSize', size, 'handedness', hand, 'userType'],
          };

          // Find matching products
          const matchingProducts = products.filter((product) => {
            const handSizeMatch =
              product.criteria.handSize.length === 0 ||
              product.criteria.handSize.includes(size);
            const handednessMatch =
              product.criteria.handedness === 'universal' ||
              product.criteria.handedness === hand;
            const userTypeMatch = product.criteria.userType.includes(userType);

            return handSizeMatch && handednessMatch && userTypeMatch;
          });

          userTypeNode.products = matchingProducts;
          handNode.children?.push(userTypeNode);
        });

        sizeNode.children?.push(handNode);
      });

      root.children?.push(sizeNode);
    });

    return root;
  };

  const tree = buildTree();

  const TreeNodeComponent: React.FC<{ node: TreeNode; depth: number }> = ({
    node,
    depth,
  }) => {
    const nodeId = node.id;
    const isExpanded = expandedNodes[nodeId] ?? depth < 3;
    const hasChildren = node.children && node.children.length > 0;
    const hasProducts = node.products && node.products.length > 0;
    const isEmpty = node.products && node.products.length === 0;

    return (
      <div className="ml-4 border-l border-gray-300">
        <div className="flex items-start gap-2 py-2 px-3">
          {hasChildren ? (
            <button
              onClick={() => toggleNode(nodeId)}
              className="flex-shrink-0 mt-1 p-0.5 text-sm font-semibold text-blue-600 hover:bg-blue-50 rounded"
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          ) : (
            <div className="flex-shrink-0 w-6" />
          )}

          <div className="flex-grow">
            <div className="font-medium text-gray-800 flex items-center gap-2">
              {node.label}
              {isEmpty && (
                <span className="inline-block w-3 h-3 bg-red-600 rounded-sm" title="No products for this selection"></span>
              )}
              {hasProducts && (
                <span
                  className={`text-sm font-semibold cursor-help relative inline-block ${
                    isEmpty ? 'text-red-600' : 'text-green-600'
                  }`}
                  onMouseEnter={() => setHoveredNode(nodeId)}
                  onMouseLeave={() => setHoveredNode(null)}
                >
                  ({node.products!.length} products)
                  {hoveredNode === nodeId && node.products && node.products.length > 0 && (
                    <div className="absolute bottom-full left-0 mb-2 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 z-50 whitespace-normal shadow-lg pointer-events-none max-w-xs">
                      <div className="font-semibold mb-1">Products:</div>
                      <div className="space-y-1">
                        {node.products.map((p, idx) => (
                          <div key={idx} className="text-gray-100">{p.model}</div>
                        ))}
                      </div>
                      <div className="absolute top-full left-3 w-2 h-2 bg-gray-900 transform rotate-45 -translate-y-1"></div>
                    </div>
                  )}
                </span>
              )}
            </div>

            {/* Product list for leaf nodes */}
            {hasProducts && isExpanded && (
              <div className="mt-2">
                {node.products!.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {node.products!.map((product) => (
                      <div
                        key={product.id}
                        className="p-2 bg-green-50 border border-green-200 rounded-md text-sm"
                      >
                        <div className="font-semibold text-green-800">
                          {product.model}
                        </div>
                        <div className="text-gray-700 text-xs">
                          {product.description}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                    ⚠️ No products match this combination
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Render children */}
        {hasChildren && isExpanded && (
          <div>
            {node.children!.map((child) => (
              <TreeNodeComponent key={child.id} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Calculate statistics
  const stats = {
    totalCombinations: 0,
    emptyPaths: 0,
    wellCoveredPaths: 0, // 5+ products
    maxProducts: 0,
    minProducts: Infinity,
  };

  const walkTree = (node: TreeNode) => {
    if (node.level === 'userType') {
      stats.totalCombinations++;
      const count = node.products?.length ?? 0;
      if (count === 0) stats.emptyPaths++;
      if (count >= 5) stats.wellCoveredPaths++;
      stats.maxProducts = Math.max(stats.maxProducts, count);
      stats.minProducts = Math.min(stats.minProducts, count);
    }
    node.children?.forEach(walkTree);
  };

  walkTree(tree);
  stats.minProducts = stats.minProducts === Infinity ? 0 : stats.minProducts;

  const exportTreeAsJSON = () => {
    const exportData: any[] = [];
    
    const collectData = (node: TreeNode, path: string[] = []) => {
      if (node.level === 'userType') {
        exportData.push({
          handSize: path[0] || '',
          handedness: path[1] || '',
          userType: path[2] || '',
          productCount: node.products?.length ?? 0,
          products: node.products?.map(p => ({ id: p.id, model: p.model })) ?? [],
        });
      }
      node.children?.forEach(child => {
        collectData(child, [...path, child.value || '']);
      });
    };
    
    collectData(tree);
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `decision-tree-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportTreeAsCSV = () => {
    const exportData: any[] = [];
    
    const collectData = (node: TreeNode, path: string[] = []) => {
      if (node.level === 'userType') {
        const productList = node.products?.map(p => p.model).join('; ') ?? 'NONE';
        exportData.push({
          handSize: path[0] || '',
          handedness: path[1] || '',
          userType: path[2] || '',
          productCount: node.products?.length ?? 0,
          products: productList,
        });
      }
      node.children?.forEach(child => {
        collectData(child, [...path, child.value || '']);
      });
    };
    
    collectData(tree);
    
    // Create CSV
    const headers = ['Hand Size', 'Handedness', 'User Type', 'Product Count', 'Products'];
    const rows = exportData.map(item => [
      item.handSize,
      item.handedness,
      item.userType,
      item.productCount,
      item.products,
    ]);
    
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const dataBlob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `decision-tree-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Decision Tree Viewer
          </h1>
          <p className="text-gray-600">
            Interactive view of all possible product combinations to help identify gaps in coverage
          </p>
        </div>

        {/* Statistics Dashboard */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Total Paths</div>
            <div className="text-3xl font-bold text-blue-600">
              {stats.totalCombinations}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Empty Paths</div>
            <div className={`text-3xl font-bold ${stats.emptyPaths > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {stats.emptyPaths}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Well Covered</div>
            <div className="text-3xl font-bold text-green-600">
              {stats.wellCoveredPaths}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Max Products</div>
            <div className="text-3xl font-bold text-purple-600">
              {stats.maxProducts}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Min Products</div>
            <div className="text-3xl font-bold text-orange-600">
              {stats.minProducts}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="bg-white rounded-lg shadow p-4 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Legend</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 bg-green-200 border border-green-400 rounded"></span>
                  <span className="text-sm text-gray-700">Products available</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 bg-red-600 rounded-sm"></span>
                  <span className="text-sm text-gray-700">No products (gap)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-blue-600 font-semibold">▼ ▶</span>
                  <span className="text-sm text-gray-700">Click to expand/collapse</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 md:flex-col">
              <button
                onClick={exportTreeAsJSON}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm whitespace-nowrap"
              >
                📥 Export JSON
              </button>
              <button
                onClick={exportTreeAsCSV}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm whitespace-nowrap"
              >
                📥 Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Tree View */}
        <div className="bg-white rounded-lg shadow p-6 overflow-x-auto">
          <TreeNodeComponent node={tree} depth={0} />
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>Use this tool to identify product coverage gaps and ensure all customer scenarios are properly served.</p>
        </div>
      </div>
    </div>
  );
};

export default DecisionTreeViewer;
